import { useState } from "react"
import { useTranslation } from "react-i18next"
import { LanguageSwitcher } from "@/components/language-switcher"
import { Link } from "react-router-dom"
import Sidebar from "@/components/side-bar"
import { Home, History, HelpCircle, FileText, User, LogOut } from "lucide-react"
import { DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger } from "@/components/ui/dropdown-menu"

import { useAuth } from "@/components/auth-provider"
import { useRouter } from "@/lib/router-helpers"
import { AnimatePresence } from "framer-motion"

export default function Header() {
  const [sidebarOpen, setSidebarOpen] = useState(false)
  const { user, logout, isAuthenticated } = useAuth()
  const router = useRouter()
  const { t } = useTranslation()

  const userInitial = user ? (user.firstname?.[0] || user.lastname?.[0] || "?").toUpperCase() : "?"

  function handleLogout() {
    logout() // logout() handles navigation with cache-busting for Safari
  }

  return (
    <>
      <div className="fixed top-0 left-0 right-0 z-50 border-b border-gray-200/80 bg-white/80 backdrop-blur-md shadow-sm transition-all duration-300">


        {/* Navigation Bar */}
        <div className="px-4 lg:px-8 py-3 lg:py-4 flex items-center justify-between lg:grid lg:grid-cols-3 ">
          {/* Left: Logos */}
          <div className="flex items-center gap-3 lg:gap-4 lg:justify-self-start">
            <Link to="/" className="flex items-center gap-3 lg:gap-4 hover:opacity-80 transition-opacity">
              <img
                src="/images/cheche.svg"
                alt="CHECHE"
                width={92}
                height={20}
                className="h-4 w-auto"
              />
              <span className="hidden lg:inline-block h-5 w-px bg-gray-300" />
              <img
                src="/images/qetero_logo_text_3.svg"
                alt="Qetero"
                width={92}
                height={20}
                className="h-5 w-auto"
              />
            </Link>
          </div>

          {/* Center: Nav (desktop) */}
          <nav className="hidden lg:flex items-center justify-center gap-8 lg:justify-self-center">
            <Link to="/" className="text-sm text-gray-700 hover:text-pink-600 transition-colors whitespace-nowrap">
              {t("home")}
            </Link>
            <Link to="/booking/history" className="text-sm text-gray-700 hover:text-pink-600 transition-colors whitespace-nowrap">
              {t("bookingHistory")}
            </Link>
            <Link to="/help" className="text-sm text-gray-700 hover:text-pink-600 transition-colors whitespace-nowrap">
              {t("help")}
            </Link>
            <Link to="/terms-and-conditions" className="text-sm text-gray-700 hover:text-pink-600 transition-colors whitespace-nowrap">
              {t("termsAndConditions")}
            </Link>
          </nav>

          {/* Right: Language + Avatar (desktop), Mobile controls */}
          <div className="flex items-center gap-3 lg:gap-4 lg:justify-self-end">
            {/* Language Selector */}
            <div className="lg:mr-1">
              <LanguageSwitcher />
            </div>

            {/* Avatar dropdown (desktop only) */}
            {isAuthenticated && (
              <div className="hidden lg:block">
                <DropdownMenu>
                  <DropdownMenuTrigger className="w-9 h-9 rounded-full bg-gradient-to-br from-pink-500 to-pink-600 flex items-center justify-center text-white text-xs font-bold shadow-md shadow-pink-200 hover:shadow-lg hover:scale-105 transition-all duration-200 outline-none ring-2 ring-white ring-offset-2 ring-offset-gray-50">
                    {userInitial}
                  </DropdownMenuTrigger>
                  <DropdownMenuContent align="end">
                    <DropdownMenuItem onClick={() => router.push("/profile")}>
                      <User className="w-4 h-4 mr-2 text-gray-800" />
                      <span suppressHydrationWarning>{t("profile")}</span>
                    </DropdownMenuItem>
                    <DropdownMenuItem className="text-pink-600" onClick={handleLogout}>
                      <LogOut className="w-4 h-4 mr-2 text-pink-600" />
                      <span suppressHydrationWarning>{t("logout")}</span>
                    </DropdownMenuItem>
                  </DropdownMenuContent>
                </DropdownMenu>
              </div>
            )}

            {/* Mobile Menu Button */}
            <button
              onClick={() => setSidebarOpen(!sidebarOpen)}
              className="lg:hidden p-2 -mr-2 text-gray-600 hover:text-pink-600 hover:bg-pink-50 rounded-full transition-colors"
              aria-label="Toggle menu"
            >
              {sidebarOpen ? (
                <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><line x1="18" y1="6" x2="6" y2="18"></line><line x1="6" y1="6" x2="18" y2="18"></line></svg>
              ) : (
                <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><line x1="4" y1="12" x2="20" y2="12"></line><line x1="4" y1="6" x2="20" y2="6"></line><line x1="4" y1="18" x2="20" y2="18"></line></svg>
              )}
            </button>
          </div>
        </div>
      </div>

      {/* Mobile Sidebar */}
      <AnimatePresence>
        {sidebarOpen && <Sidebar onClose={() => setSidebarOpen(false)} />}
      </AnimatePresence>
    </>
  )
}
